import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { AlertCircle, Info } from "lucide-react";
import { useTranslation } from "react-i18next";

interface AnalysisData {
  hard_skills?: {
    explicit?: string[];
    implicit?: string[];
  };
  work_culture?: {
    team_orientation?: number;
    formality?: number;
    work_life_balance?: number;
    notes?: string;
  };
  workload?: {
    intensity?: number;
    overtime_expectation?: number;
    notes?: string;
  };
  innovation?: {
    change_frequency?: number;
    creativity_emphasis?: number;
    notes?: string;
  };
  management?: {
    autonomy?: number;
    feedback_style?: string;
    notes?: string;
  };
  compensation?: {
    mentioned_elements?: string[];
    competitiveness_indicators?: string[];
    notes?: string;
  };
  remote_work?: {
    flexibility?: number;
    notes?: string;
  };
  key_strengths?: string[];
  potential_concerns?: string[];
  overall_recommendation?: string;
  summary?: string;
  highlights?: string[];
  skills_required?: string[];
  [key: string]: any; // Allow other properties
}

interface JobAnalysisCardProps {
  analysis: AnalysisData | null;
}

export const JobAnalysisCard = ({ analysis }: JobAnalysisCardProps) => {
  const { t } = useTranslation();

  if (!analysis) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-xl flex items-center">
            <Info className="mr-2 h-5 w-5 text-blue-500" />
            {t("jobs.analysis.title", "Job Analysis")}
          </CardTitle>
          <CardDescription>
            {t("jobs.analysis.noAI", "No AI analysis available for this job yet.")}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-xl flex items-center">
          <Info className="mr-2 h-5 w-5 text-blue-500" />
          {t("jobs.analysis.aiTitle", "AI Job Analysis")}
        </CardTitle>
        <CardDescription>
          {t("jobs.analysis.insights", "Insights extracted from the job description by AI")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Key insights at the top */}
        <div className="bg-blue-50 p-4 rounded-md">
          <h4 className="font-medium text-blue-800 mb-2">{t("jobs.analysis.summary", "Summary")}</h4>
          <p className="text-sm text-blue-700">{analysis.overall_recommendation}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Skills section */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">{t("jobs.analysis.requiredSkills", "Required Skills")}</h4>
            
            {analysis.hard_skills?.explicit && analysis.hard_skills.explicit.length > 0 && (
              <div>
                <h5 className="text-sm text-gray-600 mb-1.5">{t("jobs.analysis.explicitSkills", "Explicitly mentioned")}</h5>
                <div className="flex flex-wrap gap-1.5">
                  {analysis.hard_skills.explicit.map((skill, index) => (
                    <Badge key={index} variant="secondary">{skill}</Badge>
                  ))}
                </div>
              </div>
            )}
            
            {analysis.hard_skills?.implicit && analysis.hard_skills.implicit.length > 0 && (
              <div className="mt-3">
                <h5 className="text-sm text-gray-600 mb-1.5">{t("jobs.analysis.implicitSkills", "Implicitly required")}</h5>
                <div className="flex flex-wrap gap-1.5">
                  {analysis.hard_skills.implicit.map((skill, index) => (
                    <Badge key={index} variant="outline">{skill}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Key Strengths & Concerns */}
          <div className="space-y-3">
            {analysis.key_strengths && analysis.key_strengths.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">{t("jobs.analysis.keyStrengths", "Key Strengths")}</h4>
                <ul className="space-y-1">
                  {analysis.key_strengths.map((strength, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {analysis.potential_concerns && analysis.potential_concerns.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-900 mb-2">{t("jobs.analysis.potentialConcerns", "Potential Concerns")}</h4>
                <ul className="space-y-1">
                  {analysis.potential_concerns.map((concern, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-start">
                      <AlertCircle className="h-3.5 w-3.5 text-amber-500 mr-2 mt-0.5" />
                      {concern}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        
        <Separator />
        
        {/* Metrics section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {/* Work Culture */}
          {analysis.work_culture && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3">{t("jobs.analysis.workCulture", "Work Culture")}</h4>
              
              <div className="space-y-4">
                {analysis.work_culture.team_orientation !== undefined && (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span>{t("jobs.analysis.individualFocused", "Individual-focused")}</span>
                      <span>{t("jobs.analysis.teamOriented", "Team-oriented")}</span>
                    </div>
                    <Progress value={analysis.work_culture.team_orientation * 10} className="h-2" />
                  </div>
                )}
                
                {analysis.work_culture.formality !== undefined && (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span>{t("jobs.analysis.casual", "Casual")}</span>
                      <span>{t("jobs.analysis.formal", "Formal")}</span>
                    </div>
                    <Progress value={analysis.work_culture.formality * 10} className="h-2" />
                  </div>
                )}
                
                {analysis.work_culture.work_life_balance !== undefined && (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span>{t("jobs.analysis.workFocused", "Work-focused")}</span>
                      <span>{t("jobs.analysis.balanceFocused", "Balance-focused")}</span>
                    </div>
                    <Progress value={analysis.work_culture.work_life_balance * 10} className="h-2" />
                  </div>
                )}
                
                {analysis.work_culture.notes && (
                  <p className="text-sm text-gray-600 mt-2">{analysis.work_culture.notes}</p>
                )}
              </div>
            </div>
          )}
          
          {/* Workload & Innovation */}
          <div className="space-y-6">
            {analysis.workload && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">{t("jobs.analysis.workload", "Workload Expectations")}</h4>
                
                <div className="space-y-4">
                  {analysis.workload.intensity !== undefined && (
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span>{t("jobs.analysis.relaxed", "Relaxed")}</span>
                        <span>{t("jobs.analysis.intense", "Intense")}</span>
                      </div>
                      <Progress value={analysis.workload.intensity * 10} className="h-2" />
                    </div>
                  )}
                  
                  {analysis.workload.overtime_expectation !== undefined && (
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span>{t("jobs.analysis.rarelyExpected", "Rarely expected")}</span>
                        <span>{t("jobs.analysis.frequentlyExpected", "Frequently expected")}</span>
                      </div>
                      <Progress value={analysis.workload.overtime_expectation * 10} className="h-2" />
                    </div>
                  )}
                  
                  {analysis.workload.notes && (
                    <p className="text-sm text-gray-600 mt-2">{analysis.workload.notes}</p>
                  )}
                </div>
              </div>
            )}
            
            {analysis.management && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">{t("jobs.analysis.managementStyle", "Management Style")}</h4>
                
                <div className="space-y-4">
                  {analysis.management.autonomy !== undefined && (
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span>{t("jobs.analysis.closeSupervision", "Close supervision")}</span>
                        <span>{t("jobs.analysis.highAutonomy", "High autonomy")}</span>
                      </div>
                      <Progress value={analysis.management.autonomy * 10} className="h-2" />
                    </div>
                  )}
                  
                  {analysis.management.feedback_style && (
                    <p className="text-sm mt-2">
                      <span className="font-medium">{t("jobs.analysis.feedbackStyle", "Feedback style:")}</span> {analysis.management.feedback_style}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <Separator />
        
        {/* Bottom section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Innovation section */}
          {analysis.innovation && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3">{t("jobs.analysis.innovation", "Innovation & Adaptability")}</h4>
              
              <div className="space-y-4">
                {analysis.innovation.change_frequency !== undefined && (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span>{t("jobs.analysis.stableEnvironment", "Stable environment")}</span>
                      <span>{t("jobs.analysis.frequentChange", "Frequent change")}</span>
                    </div>
                    <Progress value={analysis.innovation.change_frequency * 10} className="h-2" />
                  </div>
                )}
                
                {analysis.innovation.creativity_emphasis !== undefined && (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span>{t("jobs.analysis.processFocused", "Process-focused")}</span>
                      <span>{t("jobs.analysis.creativityFocused", "Creativity-focused")}</span>
                    </div>
                    <Progress value={analysis.innovation.creativity_emphasis * 10} className="h-2" />
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Remote Work & Compensation */}
          <div className="space-y-6">
            {analysis.remote_work && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">{t("jobs.analysis.remoteWork", "Remote Work Flexibility")}</h4>
                
                {analysis.remote_work.flexibility !== undefined && (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span>{t("jobs.analysis.officeOnly", "Office-only")}</span>
                      <span>{t("jobs.analysis.fullyFlexible", "Fully flexible")}</span>
                    </div>
                    <Progress value={analysis.remote_work.flexibility * 10} className="h-2" />
                  </div>
                )}
                
                {analysis.remote_work.notes && (
                  <p className="text-sm text-gray-600 mt-2">{analysis.remote_work.notes}</p>
                )}
              </div>
            )}
            
            {analysis.compensation && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">{t("jobs.analysis.compensation", "Compensation")}</h4>
                
                {analysis.compensation.mentioned_elements && analysis.compensation.mentioned_elements.length > 0 && (
                  <div className="mb-2">
                    <h5 className="text-sm text-gray-600 mb-1">{t("jobs.analysis.mentionedElements", "Mentioned elements")}</h5>
                    <div className="flex flex-wrap gap-1.5">
                      {analysis.compensation.mentioned_elements.map((element, index) => (
                        <Badge key={index} variant="outline" className="bg-green-50">{element}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {analysis.compensation.notes && (
                  <p className="text-sm text-gray-600 mt-2">{analysis.compensation.notes}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
